import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Globe, MapPin, Monitor, Camera } from "lucide-react";
import PageHeader from "@/components/PageHeader";
import SectionHeading from "@/components/SectionHeading";
import Reveal from "@/components/Reveal";
import CTASection from "@/components/CTASection";

const TIERS = [
  { icon: MapPin, title: "Based in India", text: "Headquartered at #832, Ward No. 08, Mansa, Punjab — serving our home city and region since 2017." },
  { icon: MapPin, title: "Serving across India", text: "From local businesses to national brands — wherever physical presence isn't required, we work remotely across the country." },
  { icon: Globe, title: "International clients", text: "We work remotely with businesses in markets including Canada, the United States, the United Kingdom and Australia." },
  { icon: Monitor, title: "Remote, worldwide", text: "Selected digital services can be delivered to suitable clients in other countries and regions too." },
];

const MARKETS = [
  { code: "IN", name: "India", tag: "Our home market", text: "Serving businesses locally in Mansa and Punjab, and across the country — in person where it helps, remotely everywhere else." },
  { code: "CA", name: "Canada", tag: "Remote", text: "Working remotely with selected businesses — strategy, websites, SEO, advertising and content delivered across time zones." },
  { code: "US", name: "United States", tag: "Remote", text: "Selected digital services delivered remotely — with the same strategy-first process and direct communication." },
  { code: "UK", name: "United Kingdom", tag: "Remote", text: "Supporting businesses with remote digital solutions — from websites and SEO to full campaign management." },
  { code: "AU", name: "Australia", tag: "Remote", text: "Available for suitable remote projects — planned around time zones so communication stays easy." },
  { code: "+", name: "Other countries", tag: "Case by case", text: "Open to working with suitable clients from other countries and regions — if the fit is right, distance isn't the issue." },
];

const REMOTE_SERVICES = [
  "Digital Marketing Strategy", "SEO & Local SEO", "Google Ads", "Meta Advertising",
  "Social Media Marketing", "Lead Generation", "Content Creation", "Email Marketing",
  "Website Design & Development", "Branding & Creative",
];

const ServiceAreas = () => {
  useEffect(() => {
    document.title = "Service Areas — India & International — Click World Online";
  }, []);
  return (
    <>
      <PageHeader
        eyebrow="Our Service Areas"
        title={<>Based in India. <span className="text-electric">Working worldwide.</span></>}
        text="Click World Online is an Indian digital marketing agency that works with businesses locally, nationally and internationally — in person where it matters, remotely everywhere else."
        breadcrumb={[{ label: "Home", to: "/" }, { label: "Service Areas" }]}
      />

      <section className="bg-navy-deep" data-testid="reach-hierarchy-section">
        <div className="max-w-8xl mx-auto px-6 lg:px-12 py-20 lg:py-32">
          <SectionHeading
            eyebrow="How We Work"
            title={<>Local trust. <span className="text-electric">International reach.</span></>}
            text="Wherever your business is located, Click World Online can provide selected digital services remotely — allowing us to work with clients across India and international markets."
          />
          <div className="mt-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
            {TIERS.map((t, i) => (
              <Reveal key={t.title} delay={0.07 * i}>
                <div data-testid={`reach-tier-${i}`} className="relative bg-navy-light border border-white/10 rounded-3xl p-8 h-full overflow-hidden hover:border-electric/40 transition-colors duration-300">
                  <span className="font-display font-black text-5xl text-outline absolute top-5 right-6" aria-hidden="true">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <t.icon size={24} className="text-electric" />
                  <h2 className="mt-6 font-display text-xl font-extrabold text-mist tracking-tight">{t.title}</h2>
                  <p className="mt-3 text-slatec text-sm leading-relaxed">{t.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-navy grain relative" data-testid="markets-section">
        <div className="max-w-8xl mx-auto px-6 lg:px-12 py-20 lg:py-32">
          <SectionHeading
            eyebrow="Markets We Serve"
            title={<>One team. <span className="text-electric">Many markets.</span></>}
            text="India is home — and selected international markets are part of how we work every day, delivered remotely without pretending to have offices we don't."
          />
          <div className="mt-16 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {MARKETS.map((m, i) => (
              <Reveal key={m.code} delay={0.05 * i}>
                <div data-testid={`market-${m.code}`} className="flex flex-col h-full bg-navy-deep/60 border border-white/10 rounded-3xl p-8 hover:border-electric/40 transition-colors duration-300">
                  <div className="flex items-center justify-between">
                    <span className="font-display font-black text-2xl text-electric">{m.code}</span>
                    <span className="text-xs uppercase tracking-[0.2em] text-mist/40">{m.tag}</span>
                  </div>
                  <h3 className="mt-5 font-display text-2xl font-extrabold tracking-tight text-mist">{m.name}</h3>
                  <p className="mt-3 text-slatec text-sm leading-relaxed flex-1">{m.text}</p>
                  {m.code === "IN" && (
                    <Link to="/locations" data-testid="market-india-link" className="mt-5 inline-flex items-center gap-2 text-sm font-semibold text-electric hover:gap-3 transition-[gap] duration-300">
                      Cities we serve in India <ArrowUpRight size={16} />
                    </Link>
                  )}
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-mist text-navy dot-grid-light" data-testid="remote-services-section">
        <div className="max-w-8xl mx-auto px-6 lg:px-12 py-20 lg:py-32">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
            <Reveal>
              <div className="h-full bg-white border border-navy/10 rounded-3xl p-8 lg:p-10">
                <Monitor size={24} className="text-brand" />
                <h2 className="mt-5 font-display text-2xl font-extrabold tracking-tight">Delivered remotely, worldwide</h2>
                <p className="mt-3 text-navy/70 text-sm leading-relaxed">
                  These services need nothing more than good communication — available to clients across India and international markets:
                </p>
                <div className="mt-6 flex flex-wrap gap-2">
                  {REMOTE_SERVICES.map((s) => (
                    <span key={s} className="text-sm text-navy/85 border border-navy/10 bg-navy/[0.03] rounded-full px-4 py-2">
                      {s}
                    </span>
                  ))}
                </div>
              </div>
            </Reveal>
            <Reveal delay={0.1}>
              <div className="h-full bg-white border border-navy/10 rounded-3xl p-8 lg:p-10">
                <Camera size={24} className="text-brand" />
                <h2 className="mt-5 font-display text-2xl font-extrabold tracking-tight">Delivered on location, in India</h2>
                <p className="mt-3 text-navy/70 text-sm leading-relaxed">
                  Some work has to happen in person. 360° photography and Google Street View shoots are captured on-site — currently across India — then published for the world to see.
                </p>
                <div className="mt-6 flex flex-wrap gap-2">
                  <span className="text-sm text-navy/85 border border-navy/10 bg-navy/[0.03] rounded-full px-4 py-2">360° Photography & Virtual Tours</span>
                  <span className="text-sm text-navy/85 border border-navy/10 bg-navy/[0.03] rounded-full px-4 py-2">Google Street View Shoots</span>
                  <span className="text-sm text-navy/85 border border-navy/10 bg-navy/[0.03] rounded-full px-4 py-2">On-site Content Shoots</span>
                </div>
                <Link to="/360-experiences" data-testid="remote-360-link" className="mt-6 inline-flex items-center gap-2 text-sm font-semibold text-brand hover:gap-3 transition-[gap] duration-300">
                  See 400+ published tours <ArrowUpRight size={16} />
                </Link>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      <CTASection
        title="Wherever you are — let's talk."
        text="Local to Mansa, anywhere in India, or halfway across the world — if your business needs digital that works, we should talk."
      />
    </>
  );
};

export default ServiceAreas;
